import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import RegisterForm from "./components/RegisterForm";
import LoginForm from "./components/Login.js";
import GlobalAdminDashboard from "./components/GlobalAdminDashboard";
import ProfilePage from "./components/ProfilePage.js";
import NavBar from "./components/NavBar.js";
import MobileNavBar from "./components/MobileNavBar.js";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { NavigationProvider, useNavigation } from "./contexts/NavigationContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import "./App.css";
import UserDashboard from "./components/UserDashboard.js";
import EmailVerification from "./components/EmailVerification";
import PerformersManagement from "./components/communities/PerformersManagement.js";
import NotificationsPage from './pages/NotificationsPage';
import MobileNotificationsPage from './pages/MobileNotificationsPage';
import Posts from "./pages/Posts";
import SinglePost from "./components/SinglePost";
import CommunitiesList from './components/communities/CommunitiesList';
import CreateCommunity from './components/communities/CreateCommunity';
import JoinCommunity from './components/communities/JoinCommunity';
import ManageCommunity from './components/communities/ManageCommunity';
import CommunityInvite from './components/communities/CommunityInvite';
import ResponsiveCommunityView from './components/communities/ResponsiveCommunityView';
import ResponsiveSongLibrary from './components/ResponsiveSongLibrary.js';
import AcceptInvitation from './components/communities/AcceptInvitation';
import InvitationError from './components/communities/InvitationError';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import LandingPage from './components/LandingPage';

function ProtectedRoute({ children, role }) {
    const { user } = useAuth();
    if (!user) return <Navigate to="/login" />;
    if (role && user.role !== role) return <Navigate to="/dashboard" />;
    return children;
}

// Component to render the appropriate navbar based on device type
function ResponsiveNavigation() {
    const { isMobile } = useNavigation();
    
    // Conditionally render only the appropriate navbar
    if (isMobile) {
        return <MobileNavBar />;
    } else {
        return <NavBar />;
    }
}

// Component to render the appropriate notifications page based on device type
function ResponsiveNotifications() {
    const { isMobile } = useNavigation();
    const { user } = useAuth();
    
    if (!user) return <Navigate to="/login" />;
    
    // Conditionally render the appropriate notifications page
    return isMobile ? <MobileNotificationsPage /> : <NotificationsPage />;
}

// Main app content with routes (authenticated pages with navbar)
function AuthenticatedContent() {
    const { isMobile } = useNavigation();

    return (
        <>
            <ResponsiveNavigation />
            <div className={`container mt-4 ${isMobile ? 'mobile-content' : ''}`}>
                <Routes>
                    <Route path="/dashboard" element={<ProtectedRoute><UserDashboard /></ProtectedRoute>} />
                    <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
                    <Route path="/posts" element={<ProtectedRoute><Posts /></ProtectedRoute>} />
                    <Route path="/posts/:postId" element={<ProtectedRoute><SinglePost /></ProtectedRoute>} />
                    <Route path="/songs" element={<ProtectedRoute><ResponsiveSongLibrary /></ProtectedRoute>} />
                    <Route path="/notifications" element={<ResponsiveNotifications />} />
                    <Route path="/performers" element={<ProtectedRoute role="community_admin"><PerformersManagement /></ProtectedRoute>}/>
                    <Route path="/community/list" element={<CommunitiesList />} />
                    <Route path="/community/create" element={<CreateCommunity />} />
                    <Route path="/community/join" element={<JoinCommunity />} />
                    <Route path="/community/:communityId" element={<ResponsiveCommunityView />} />
                    <Route path="/manage-community/:communityId" element={<ManageCommunity />} />
                    <Route path="/community/:communityId/invite" element={<CommunityInvite />} />
                    <Route path="/global-admin" element={<ProtectedRoute role="global_admin"><GlobalAdminDashboard /></ProtectedRoute>} />
                </Routes>
            </div>
        </>
    );
}

// Main app content with all routes
function AppContent() {
    const { user } = useAuth();

    return (
        <Routes>
            {/* Landing page - redirect to dashboard if logged in */}
            <Route path="/" element={<LandingPage />} />

            {/* Public routes without navbar */}
            <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <LoginForm />} />
            <Route path="/register" element={user ? <Navigate to="/dashboard" /> : <RegisterForm />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/verify-email/:token" element={<EmailVerification />} />
            <Route path="/accept-invitation" element={<AcceptInvitation />} />
            <Route path="/invitation" element={<AcceptInvitation />} />
            <Route path="/invitation-error" element={<InvitationError />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />

            {/* Authenticated routes with navbar */}
            <Route path="/*" element={<AuthenticatedContent />} />
        </Routes>
    );
}

function App() {
    return (
        <AuthProvider>
            <LanguageProvider>
                <Router>
                    <NavigationProvider>
                        <AppContent />
                    </NavigationProvider>
                </Router>
            </LanguageProvider>
        </AuthProvider>
    );
}

export default App;